/**
 * feedAggregator.ts — Cross-board aggregation engine for Chanelle OS.
 *
 * Pulls tasks assigned to a user and mentions/updates where the user is
 * tagged across all registry boards (Data Sources), normalizes them into
 * FeedRecord objects, deduplicates, and writes them into the Task Feed board.
 *
 * This module is the core "intelligence plumbing" that populates the single
 * global personal feed for Chanelle, consumed by My Work and Inbox widgets.
 *
 * Board references:
 *   - Task Feed:       5092855258  (output — normalized records land here)
 *   - Aggregator:      5092855497  (this board holds aggregation run metadata)
 *   - Data Sources:    5092854138  (input — registry of boards to pull from)
 *
 * User:
 *   - Chanelle Eveleigh, monday user ID: 77798046
 */

import { gqlClient } from '../api/client';
import { mutateCreateItem } from '../api/mutations';
import { queryBoardItems, columnMap } from '../api/queries';
import { scoreItem } from '../utils/priorityScore';
import type {
  MondayItem,
  FeedRecord,
  FeedRecordType,
  DataSourceEntry,
  AggregationResult,
  Action,
} from '../types';

// ─── Constants ───────────────────────────────────────────────────────────────

/** Hard-coded board IDs as provided by the user */
export const TASK_FEED_BOARD_ID = '5092855258';
export const AGGREGATOR_BOARD_ID = '5092855497';
export const DATA_SOURCES_BOARD_ID = '5092854138';

/** Chanelle's monday.com user ID */
export const CHANELLE_USER_ID = '77798046';

// ─── Data Source Registry ────────────────────────────────────────────────────

/**
 * Read all entries from the Data Sources board.
 * Each item represents a board to aggregate from, with columns:
 *   - Name: board display name
 *   - Board ID (text): the monday.com board ID
 *   - Workspace (text): workspace name for provenance
 *   - Include Tasks (checkbox): whether to pull assigned tasks
 *   - Include Mentions (checkbox): whether to pull mentions
 *   - Last Sync (date): last aggregation timestamp
 */
export async function queryDataSources(): Promise<DataSourceEntry[]> {
  const items = await queryBoardItems(DATA_SOURCES_BOARD_ID, 200);

  return items.map((item) => {
    const cv = columnMap(item);
    const includeTasksRaw = cv['include_tasks'] ?? cv['checkbox'] ?? '';
    const includeMentionsRaw = cv['include_mentions'] ?? '';

    return {
      id: item.id,
      name: item.name,
      boardId: cv['board_id'] ?? cv['text'] ?? '',
      workspace: cv['workspace'] ?? cv['text0'] ?? '',
      includeTasks:
        includeTasksRaw === 'true' ||
        includeTasksRaw === 'Yes' ||
        includeTasksRaw === 'v',
      includeMentions:
        includeMentionsRaw === 'true' ||
        includeMentionsRaw === 'Yes' ||
        includeMentionsRaw === 'v' ||
        // Default: include mentions if tasks are included
        includeTasksRaw === 'true' ||
        includeTasksRaw === 'Yes',
      lastSyncAt: cv['last_sync'] ?? cv['date'] ?? undefined,
    };
  });
}

// ─── Task Pulling ────────────────────────────────────────────────────────────

/**
 * Pull tasks assigned to a specific user from a single board.
 * Uses the person column filter to get only items assigned to the user.
 */
const GET_USER_TASKS_FROM_BOARD = `
  query GetUserTasksFromBoard($boardId: ID!, $userId: String!, $limit: Int!) {
    boards(ids: [$boardId]) {
      id
      name
      items_page(
        limit: $limit
        query_params: {
          rules: [{ column_id: "person", compare_value: [$userId] }]
        }
      ) {
        items {
          id
          name
          board { id name }
          group { id title }
          created_at
          updated_at
          column_values {
            id
            text
            value
            column { title type }
          }
        }
      }
    }
  }
`;

export async function pullTasksFromBoard(
  boardId: string,
  userId: string = CHANELLE_USER_ID,
  limit = 100,
): Promise<MondayItem[]> {
  try {
    const data = await gqlClient<{
      boards: Array<{
        id: string;
        name: string;
        items_page: { items: MondayItem[] };
      }>;
    }>(GET_USER_TASKS_FROM_BOARD, { boardId, userId: `${userId}`, limit });

    return data.boards?.[0]?.items_page?.items ?? [];
  } catch (err) {
    console.warn(`[FeedAggregator] Failed to pull tasks from board ${boardId}:`, err);
    return [];
  }
}

// ─── Mention / Update Pulling ────────────────────────────────────────────────

/**
 * Pull recent updates/mentions from a board that reference the user.
 * We fetch all updates from the board and filter for mentions client-side.
 */
const GET_BOARD_UPDATES = `
  query GetBoardUpdates($boardId: ID!, $limit: Int!) {
    boards(ids: [$boardId]) {
      id
      name
      updates(limit: $limit) {
        id
        text_body
        created_at
        creator {
          id
          name
        }
        item_id
      }
    }
  }
`;

interface BoardUpdate {
  id: string;
  text_body: string;
  created_at: string;
  creator: { id: string; name: string };
  item_id?: string;
}

export async function pullMentionsFromBoard(
  boardId: string,
  userId: string = CHANELLE_USER_ID,
  userName = 'Chanelle',
  limit = 50,
): Promise<Array<BoardUpdate & { boardId: string; boardName: string }>> {
  try {
    const data = await gqlClient<{
      boards: Array<{
        id: string;
        name: string;
        updates: BoardUpdate[];
      }>;
    }>(GET_BOARD_UPDATES, { boardId, limit });

    const board = data.boards?.[0];
    if (!board) return [];

    // Filter updates that mention the user (by @mention, name, or user ID)
    const mentionPatterns = [
      `@${userName.toLowerCase()}`,
      `@${userId}`,
      userName.toLowerCase(),
    ];

    return (board.updates ?? [])
      .filter((u) => {
        const text = (u.text_body ?? '').toLowerCase();
        // Don't include user's own updates
        if (u.creator?.id === userId) return false;
        return mentionPatterns.some((pattern) => text.includes(pattern));
      })
      .map((u) => ({
        ...u,
        boardId: board.id,
        boardName: board.name,
      }));
  } catch (err) {
    console.warn(`[FeedAggregator] Failed to pull mentions from board ${boardId}:`, err);
    return [];
  }
}

// ─── Normalization ───────────────────────────────────────────────────────────

/**
 * Convert a raw MondayItem (task) into a FeedRecord.
 */
function normalizeTask(item: MondayItem, userId: string): FeedRecord {
  const cv = columnMap(item);
  const boardId = item.board?.id ?? '';
  const boardName = item.board?.name ?? '';

  // Build an Action for scoring
  const action: Action = {
    id: item.id,
    name: item.name,
    boardId,
    boardName,
    priority: (cv['priority'] || cv['status'] || undefined) as Action['priority'],
    status: cv['status4'] || cv['status'] || cv['color'] || undefined,
    dueDate: cv['date4'] || cv['due_date'] || cv['date'] || undefined,
    createdAt: item.created_at,
    updatedAt: item.updated_at,
  };

  return {
    name: item.name,
    recordType: 'task' as FeedRecordType,
    sourceBoardId: boardId,
    sourceBoardName: boardName,
    sourceItemId: item.id,
    assigneeUserId: userId,
    status: action.status,
    priority: action.priority,
    dueDate: action.dueDate,
    createdAt: item.created_at,
    updatedAt: item.updated_at,
    dedupKey: `${boardId}_${item.id}_task`,
    score: scoreItem(action),
  };
}

/**
 * Convert a board update/mention into a FeedRecord.
 */
function normalizeMention(
  update: BoardUpdate & { boardId: string; boardName: string },
  userId: string,
): FeedRecord {
  return {
    name: `Mention from ${update.creator?.name ?? 'Unknown'}`,
    recordType: 'mention' as FeedRecordType,
    sourceBoardId: update.boardId,
    sourceBoardName: update.boardName,
    sourceItemId: update.item_id ?? update.id,
    assigneeUserId: userId,
    mentionSource: update.creator?.name,
    mentionText: (update.text_body ?? '').slice(0, 500),
    createdAt: update.created_at,
    updatedAt: update.created_at,
    dedupKey: `${update.boardId}_${update.id}_mention`,
    score: 50, // Mentions get a moderate default score
  };
}

// ─── Deduplication ───────────────────────────────────────────────────────────

/**
 * Read existing feed records from the Task Feed board and return a Set
 * of dedup keys already present.
 */
export async function getExistingDedupKeys(): Promise<Set<string>> {
  const items = await queryBoardItems(TASK_FEED_BOARD_ID, 500);
  const keys = new Set<string>();

  for (const item of items) {
    const cv = columnMap(item);
    const key = cv['dedup_key'] ?? cv['text'] ?? '';
    if (key) keys.add(key);
  }

  return keys;
}

/**
 * Deduplicate feed records against existing keys.
 */
function deduplicateRecords(
  records: FeedRecord[],
  existingKeys: Set<string>,
): FeedRecord[] {
  const seen = new Set<string>(existingKeys);
  const unique: FeedRecord[] = [];

  for (const record of records) {
    if (!seen.has(record.dedupKey)) {
      seen.add(record.dedupKey);
      unique.push(record);
    }
  }

  return unique;
}

// ─── Task Feed Board Write ───────────────────────────────────────────────────

/**
 * Write a single FeedRecord to the Task Feed board.
 *
 * Task Feed board schema (expected columns):
 *   - Name (item name):  record.name
 *   - Record Type (status): task | mention | update
 *   - Source Board ID (text): record.sourceBoardId
 *   - Source Board Name (text): record.sourceBoardName
 *   - Source Item ID (text): record.sourceItemId
 *   - Source Workspace (text): record.sourceWorkspace
 *   - Assignee User ID (text): record.assigneeUserId
 *   - Status (status): record.status
 *   - Priority (status): record.priority
 *   - Due Date (date): record.dueDate
 *   - Mention Source (text): record.mentionSource
 *   - Mention Text (long_text): record.mentionText
 *   - Dedup Key (text): record.dedupKey
 *   - Score (numbers): record.score
 *   - Created At (date): record.createdAt
 *   - Updated At (date): record.updatedAt
 */
export async function writeFeedRecord(record: FeedRecord): Promise<string | null> {
  try {
    const columnValues: Record<string, unknown> = {};

    // Text columns
    if (record.sourceBoardId) columnValues['text'] = record.dedupKey; // Dedup key in first text col
    if (record.sourceBoardId) columnValues['text0'] = record.sourceBoardId;
    if (record.sourceBoardName) columnValues['text6'] = record.sourceBoardName;
    if (record.sourceItemId) columnValues['text7'] = record.sourceItemId;
    if (record.sourceWorkspace) columnValues['text8'] = record.sourceWorkspace;
    if (record.assigneeUserId) columnValues['text9'] = record.assigneeUserId;
    if (record.mentionSource) columnValues['text00'] = record.mentionSource;

    // Status columns
    if (record.recordType) columnValues['status'] = { label: record.recordType };
    if (record.status) columnValues['status4'] = { label: record.status };
    if (record.priority) columnValues['color'] = { label: record.priority };

    // Date columns
    if (record.dueDate) columnValues['date4'] = { date: record.dueDate };

    // Long text
    if (record.mentionText) columnValues['long_text'] = { text: record.mentionText };

    // Numbers
    if (record.score != null) columnValues['numbers'] = String(record.score);

    const item = await mutateCreateItem({
      boardId: TASK_FEED_BOARD_ID,
      itemName: record.name,
      columnValues,
    });

    return item?.id ?? null;
  } catch (err) {
    console.warn('[FeedAggregator] Failed to write feed record:', record.name, err);
    return null;
  }
}

// ─── Main Aggregation Pipeline ───────────────────────────────────────────────

/**
 * Run the full aggregation pipeline:
 *
 * 1. Read Data Sources registry
 * 2. For each source with Include Tasks = enabled, pull tasks assigned to user
 * 3. For each source, pull mentions/updates where user is tagged
 * 4. Normalize all records into FeedRecord format
 * 5. Deduplicate against existing Task Feed records
 * 6. Write new records to Task Feed board
 * 7. Return aggregation summary
 */
export async function runAggregation(
  userId: string = CHANELLE_USER_ID,
  userName = 'Chanelle',
): Promise<AggregationResult> {
  const errors: string[] = [];
  const allRecords: FeedRecord[] = [];

  console.log('[FeedAggregator] Starting cross-board aggregation…');

  // 1. Read data sources
  let sources: DataSourceEntry[] = [];
  try {
    sources = await queryDataSources();
    console.log(`[FeedAggregator] Found ${sources.length} data source(s)`);
  } catch (err) {
    errors.push(`Failed to read Data Sources: ${(err as Error).message}`);
    // If we can't read the registry, fall back to pulling from ALL boards
    console.warn('[FeedAggregator] Falling back to all-boards mode');
  }

  // 2 & 3. Pull tasks and mentions from each source
  const activeSources = sources.filter(
    (s) => s.boardId && (s.includeTasks || s.includeMentions),
  );

  for (const source of activeSources) {
    // Pull tasks
    if (source.includeTasks) {
      try {
        const items = await pullTasksFromBoard(source.boardId, userId);
        const taskRecords = items.map((item) => ({
          ...normalizeTask(item, userId),
          sourceWorkspace: source.workspace,
        }));
        allRecords.push(...taskRecords);
        console.log(
          `[FeedAggregator] ${source.name}: ${items.length} task(s)`,
        );
      } catch (err) {
        errors.push(`Tasks from ${source.name}: ${(err as Error).message}`);
      }
    }

    // Pull mentions
    if (source.includeMentions) {
      try {
        const mentions = await pullMentionsFromBoard(
          source.boardId,
          userId,
          userName,
        );
        const mentionRecords = mentions.map((m) => ({
          ...normalizeMention(m, userId),
          sourceWorkspace: source.workspace,
        }));
        allRecords.push(...mentionRecords);
        console.log(
          `[FeedAggregator] ${source.name}: ${mentions.length} mention(s)`,
        );
      } catch (err) {
        errors.push(`Mentions from ${source.name}: ${(err as Error).message}`);
      }
    }
  }

  const tasksFound = allRecords.filter((r) => r.recordType === 'task').length;
  const mentionsFound = allRecords.filter((r) => r.recordType === 'mention').length;

  console.log(
    `[FeedAggregator] Total raw records: ${allRecords.length} ` +
      `(${tasksFound} tasks, ${mentionsFound} mentions)`,
  );

  // 4. Deduplicate
  let existingKeys = new Set<string>();
  try {
    existingKeys = await getExistingDedupKeys();
    console.log(`[FeedAggregator] ${existingKeys.size} existing feed records`);
  } catch (err) {
    errors.push(`Dedup read failed: ${(err as Error).message}`);
  }

  const newRecords = deduplicateRecords(allRecords, existingKeys);
  const duplicatesSkipped = allRecords.length - newRecords.length;

  console.log(
    `[FeedAggregator] After dedup: ${newRecords.length} new, ${duplicatesSkipped} skipped`,
  );

  // 5. Write new records to Task Feed
  let written = 0;
  for (const record of newRecords) {
    const id = await writeFeedRecord(record);
    if (id) {
      written++;
    }
  }

  const syncedAt = new Date().toISOString();
  console.log(
    `[FeedAggregator] Aggregation complete: ${written}/${newRecords.length} written`,
  );

  // 6. Update aggregator board with run metadata
  try {
    await mutateCreateItem({
      boardId: AGGREGATOR_BOARD_ID,
      itemName: `Sync ${syncedAt.split('T')[0]}`,
      columnValues: {
        text: `${written} records written`,
        numbers: String(written),
        status: { label: errors.length === 0 ? 'Success' : 'Partial' },
        date4: { date: syncedAt.split('T')[0] },
      },
    });
  } catch {
    // Non-critical — just log metadata
  }

  return {
    totalSourceBoards: activeSources.length,
    tasksFound,
    mentionsFound,
    newRecordsWritten: written,
    duplicatesSkipped,
    errors,
    syncedAt,
  };
}

// ─── Task Feed Read ──────────────────────────────────────────────────────────

/**
 * Read all feed records from the Task Feed board.
 * Returns normalized FeedRecord objects sorted by score (highest first).
 */
export async function queryTaskFeed(
  userId: string = CHANELLE_USER_ID,
  limit = 200,
): Promise<FeedRecord[]> {
  const items = await queryBoardItems(TASK_FEED_BOARD_ID, limit);

  const records: FeedRecord[] = items.map((item) => {
    const cv = columnMap(item);

    return {
      id: item.id,
      name: item.name,
      recordType: (cv['status'] ?? 'task') as FeedRecordType,
      sourceBoardId: cv['text0'] ?? cv['source_board_id'] ?? '',
      sourceBoardName: cv['text6'] ?? cv['source_board_name'] ?? '',
      sourceItemId: cv['text7'] ?? cv['source_item_id'] ?? '',
      sourceWorkspace: cv['text8'] ?? cv['source_workspace'] ?? '',
      assigneeUserId: cv['text9'] ?? cv['assignee_user_id'] ?? userId,
      status: cv['status4'] ?? cv['status'] ?? undefined,
      priority: cv['color'] ?? cv['priority'] ?? undefined,
      dueDate: cv['date4'] ?? cv['due_date'] ?? undefined,
      mentionSource: cv['text00'] ?? cv['mention_source'] ?? undefined,
      mentionText: cv['long_text'] ?? cv['mention_text'] ?? undefined,
      createdAt: item.created_at,
      updatedAt: item.updated_at,
      dedupKey: cv['text'] ?? cv['dedup_key'] ?? `${item.id}_unknown`,
      score: cv['numbers'] ? Number(cv['numbers']) : 0,
    };
  });

  // Filter to records for this user and sort by score desc
  return records
    .filter((r) => r.assigneeUserId === userId || !r.assigneeUserId)
    .sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
}

/**
 * Get only task-type feed records (for My Work widget).
 */
export async function queryTaskFeedTasks(
  userId?: string,
  limit?: number,
): Promise<FeedRecord[]> {
  const all = await queryTaskFeed(userId, limit);
  return all.filter((r) => r.recordType === 'task');
}

/**
 * Get only mention/update-type feed records (for Inbox widget).
 */
export async function queryTaskFeedMentions(
  userId?: string,
  limit?: number,
): Promise<FeedRecord[]> {
  const all = await queryTaskFeed(userId, limit);
  return all.filter((r) => r.recordType === 'mention' || r.recordType === 'update');
}
